import { useEffect, useRef } from 'react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Link } from 'react-router-dom';
import Container from '@/components/base/Container';
import Eyebrow from '@/components/base/Eyebrow';
import { services } from '@/mocks/services';
import type { reviews } from '@/mocks/reviews';

gsap.registerPlugin(ScrollTrigger);

interface Scene { src: string; alt: string }
interface Props {
  items: typeof reviews;
  scenes: Scene[];
  anySample: boolean;
}

/**
 * Desktop "flip calendar": the section sticks to the viewport while the user
 * scrolls. Each page reveals its parts one by one, then lifts up and away
 * before the next page rises in from below.
 */
export default function TestimonialsCalendar({ items, scenes, anySample }: Props) {
  const sectionRef = useRef<HTMLElement>(null);
  const pageRefs = useRef<(HTMLDivElement | null)[]>([]);
  const counterRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const section = sectionRef.current;
    const pages = pageRefs.current.filter(Boolean) as HTMLDivElement[];
    if (!section || !pages.length) return undefined;

    const ctx = gsap.context(() => {
      pages.forEach((p, i) => {
        gsap.set(p, { yPercent: i === 0 ? 0 : 100, autoAlpha: i === 0 ? 1 : 0 });
        gsap.set(p.querySelector('[data-img]'), { clipPath: 'inset(100% 0% 0% 0%)' });
        gsap.set(p.querySelectorAll('[data-step]'), { autoAlpha: 0, y: 40 });
      });

      const tl = gsap.timeline({
        defaults: { ease: 'none' },
        scrollTrigger: {
          trigger: section,
          start: 'top top',
          end: 'bottom bottom',
          scrub: 1.4,
          invalidateOnRefresh: true,
          onUpdate: (self) => {
            const n = Math.min(pages.length, Math.floor(self.progress * pages.length) + 1);
            if (counterRef.current) counterRef.current.textContent = `0${n} / 0${pages.length}`;
          },
        },
      });

      // Page 1 reveals on a timed intro as soon as the section enters view,
      // so the area is never empty while the user is arriving.
      const first = pages[0];
      gsap.timeline({
        scrollTrigger: { trigger: section, start: 'top 80%', once: true },
      })
        .to(first.querySelector('[data-img]'), { clipPath: 'inset(0% 0% 0% 0%)', duration: 1, ease: 'power3.inOut' })
        .fromTo(first.querySelector('[data-img] img'), { scale: 1.15 }, { scale: 1, duration: 1.3, ease: 'power2.out' }, '<')
        .to(first.querySelectorAll('[data-step]'), { autoAlpha: 1, y: 0, duration: 0.6, stagger: 0.12, ease: 'power2.out' }, '<0.35');

      pages.forEach((p, i) => {
        const img = p.querySelector('[data-img]');
        const imgInner = p.querySelector('[data-img] img');
        const steps = p.querySelectorAll('[data-step]');
        if (i === 0) {
          tl.to({}, { duration: 0.8 });
          if (pages.length > 1) tl.to(p, { yPercent: -35, autoAlpha: 0, duration: 0.6, ease: 'power3.in' });
          return;
        }
        if (i > 0) {
          tl.to(p, { yPercent: 0, autoAlpha: 1, duration: 0.6, ease: 'power3.out' }, '<');
        }
        tl.to(img, { clipPath: 'inset(0% 0% 0% 0%)', duration: 0.6, ease: 'power3.inOut' }, i > 0 ? '<0.15' : 0)
          .fromTo(imgInner, { scale: 1.15 }, { scale: 1, duration: 0.8, ease: 'power2.out', immediateRender: false }, '<')
          .to(steps, { autoAlpha: 1, y: 0, duration: 0.4, stagger: 0.15, ease: 'power2.out' }, '<0.25')
          .to({}, { duration: 0.5 });
        if (i < pages.length - 1) {
          tl.to(p, { yPercent: -35, autoAlpha: 0, duration: 0.6, ease: 'power3.in' });
        }
      });
    }, section);

    const refresh = () => ScrollTrigger.refresh();
    window.addEventListener('load', refresh);
    return () => {
      window.removeEventListener('load', refresh);
      ctx.revert();
    };
  }, []);

  return (
    <section ref={sectionRef} className="relative bg-background-100" style={{ height: `${items.length * 150}vh` }}>
      <div className="sticky top-0 flex h-screen flex-col overflow-hidden py-16">
        <Container className="flex h-full flex-col">
          <div className="flex items-end justify-between gap-10">
            <div>
              <Eyebrow label="Customer Experiences" />
              <h2 className="mt-5 text-[48px] font-medium leading-[1.05] tracking-[-0.025em] text-foreground-950">
                What a Good Visit Feels Like.
              </h2>
            </div>
            {anySample && (
              <p className="inline-flex items-center gap-2 rounded-full bg-accent-100 px-3 py-1.5 text-xs text-accent-900">
                <i className="ri-information-line" />
                Sample reviews for design preview. Not verified customer feedback.
              </p>
            )}
          </div>

          <div className="relative mt-10 min-h-0 flex-1">
            {items.map((item, i) => {
              const category = services.find((s) => s.slug === item.serviceSlug)?.name ?? '';
              const scene = scenes[i % scenes.length];
              return (
                <div key={item.id ?? i} ref={(el) => { pageRefs.current[i] = el; }} className="invisible absolute inset-0 grid grid-cols-2 gap-16 will-change-transform">
                  <div data-img className="relative h-full w-full overflow-hidden rounded-xl bg-background-200">
                    <img src={scene.src} alt={scene.alt} title="Customer experiences with MENDWELL" className="h-full w-full object-cover object-top" />
                  </div>
                  <blockquote className="flex flex-col justify-center">
                    <span data-step className="text-[96px] font-light leading-none tracking-tight text-background-300">{`0${i + 1}`}</span>
                    {category && <p data-step className="mt-8 text-[11px] uppercase tracking-[0.16em] text-primary-600">{category}</p>}
                    <p data-step className="mt-4 text-[28px] font-light leading-snug tracking-tight text-foreground-950">&ldquo;{item.text}&rdquo;</p>
                    <footer data-step className="mt-8 flex flex-wrap items-center gap-4 text-xs uppercase tracking-[0.14em] text-foreground-600">
                      <span>{item.displayName}</span>
                      {typeof item.rating === 'number' && (
                        <span className="flex items-center gap-0.5 text-primary-500" aria-label={`Rated ${item.rating} out of 5`}>
                          {Array.from({ length: 5 }).map((_, s) => (
                            <i key={s} className={s < (item.rating ?? 0) ? 'ri-star-fill' : 'ri-star-line text-background-400'} />
                          ))}
                        </span>
                      )}
                    </footer>
                  </blockquote>
                </div>
              );
            })}
          </div>

          <div className="mt-8 flex items-center justify-between border-t border-background-300/60 pt-5">
            <span ref={counterRef} className="text-sm text-foreground-600">{`01 / 0${items.length}`}</span>
            <Link to="/reviews" className="inline-flex items-center gap-2 whitespace-nowrap text-sm text-foreground-950 mw-link">
              View All Reviews <span className="mw-arrow">&rarr;</span>
            </Link>
          </div>
        </Container>
      </div>
    </section>
  );
}